import { ApiClient } from '../client/ApiClient';
import { ApiResponse, HttpMethod } from '../types';
import { CountryCode } from '../utils/multiTenancyHeaders';
import { KlaviyoResponse } from './types';

export class KlaviyoClient extends ApiClient {
  private readonly _klaviyoSubscribeUrl: string;
  private readonly _klaviyoEnvironment: string;
  private readonly _country: CountryCode;

  constructor({
    klaviyoEnvironment,
    klaviyoSubscribeUrl,
    countryCode,
  }: {
    klaviyoSubscribeUrl: string;
    klaviyoEnvironment: string;
    countryCode: CountryCode;
  }) {
    super();
    this._klaviyoEnvironment = klaviyoEnvironment;
    this._klaviyoSubscribeUrl = klaviyoSubscribeUrl;
    this._country = countryCode;
  }

  protected get requestHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      revision: '2024-10-15',
    };
  }

  private buildProfile(email: string) {
    return {
      data: {
        type: 'profile',
        attributes: { email },
      },
    };
  }

  private buildEventBody(email: string) {
    return {
      type: 'event',
      attributes: {
        profile: this.buildProfile(email),
        metric: {
          data: {
            type: 'metric',
            attributes: {
              name: 'Subscribed to JB Marketing',
            },
          },
        },
        properties: {
          'Subscribe date': new Date().toISOString(),
          'Subscribe source': 'Website - Account creation',
        },
      },
    };
  }

  private buildSubscriptionBody(email: string, listId?: string) {
    return {
      type: 'subscription',
      attributes: {
        profile: this.buildProfile(email),
      },
      relationships: {
        list: {
          data: {
            type: 'list',
            id: listId,
          },
        },
      },
    };
  }

  private getSubscribePayload(email: string, listId?: string) {
    const path = `${this._klaviyoSubscribeUrl}?company_id=${this._klaviyoEnvironment}`;
    const body =
      this._country === 'AU'
        ? { data: this.buildEventBody(email) }
        : { data: this.buildSubscriptionBody(email, listId) };

    return {
      path,
      method: 'POST' as HttpMethod,
      body,
    };
  }

  subscribe = async ({ email, listId }: { email: string; listId: string }): Promise<ApiResponse<KlaviyoResponse>> => {
    const payload = this.getSubscribePayload(email, listId);
    const { data, statusCode, error } = await this._fetch<KlaviyoResponse>(payload);
    if (error) {
      console.log('error from KlaviyoClient', { error, statusCode });
      return { error, statusCode };
    }

    return { data, statusCode };
  };
}
